import { z } from "zod";
import { AppearanceChoice } from "./actor";
import { Cents, DistrictId, IsoUtc, PropertyId, Uuid } from "./primitives";
import { InteriorBoard, PlotOffer } from "./properties";
import { StoreThemeId } from "./world";

/**
 * Wallet DTOs. Coins and dollar Credit are SEPARATE ledgers (docs/04 section 1): never summed,
 * never converted, always labelled. Balances are read from the server's journal-backed projection.
 */
export const WalletsResponse = z.object({
  coins: z.object({ available: z.number().int().min(0), reserved: z.number().int().min(0) }),
  credit: z.object({ availableCents: Cents, reservedCents: Cents }),
  asOf: IsoUtc,
  /** How coins are earned right now (honest copy for the UI). */
  earning: z.object({ available: z.boolean(), message: z.string() }),
});
export type WalletsResponse = z.infer<typeof WalletsResponse>;

export const WalletHistoryItem = z.object({
  journalId: Uuid,
  unit: z.enum(["COIN", "CREDIT_USD"]),
  type: z.string(),
  /** Signed change to the account holder's available balance in the unit's integer amount. */
  amount: z.number().int(),
  description: z.string(),
  at: IsoUtc,
});
export const WalletHistoryResponse = z.object({ items: z.array(WalletHistoryItem), nextCursor: z.string().nullable() });
export type WalletHistoryResponse = z.infer<typeof WalletHistoryResponse>;

// ---------------- Real Estate office ----------------
export const RealEstateListing = z.object({
  propertyId: PropertyId,
  address: z.string(),
  street: z.string(),
  /** World v5 (ADR-0041): the plot's district and its name from the manifest ("riverside", "Riverside"). */
  districtId: DistrictId,
  districtName: z.string().min(1).max(60),
  offer: PlotOffer,
});
export type RealEstateListing = z.infer<typeof RealEstateListing>;

export const RealEstateListingsResponse = z.object({
  /** Grouped by district in manifest order (Central, Riverside, Hillcrest), each district in property order. */
  items: z.array(RealEstateListing),
  /** Town-wide: the limit counts plots in every district together. */
  plotsPerAccount: z.number().int().min(1),
  /** Residential plots the signed-in account already owns ([] for guests). */
  ownedPropertyIds: z.array(PropertyId),
  /** Why buying is not possible for this visitor right now (null = can buy if funds suffice). */
  blockedReason: z
    .enum(["SIGN_IN_REQUIRED", "EMAIL_NOT_VERIFIED", "PLOT_LIMIT_REACHED", "FEATURE_DISABLED"])
    .nullable(),
  /** Dollar purchases need the payments milestone; the UI shows the price but keeps the option disabled. */
  dollarPurchasesEnabled: z.boolean(),
});
export type RealEstateListingsResponse = z.infer<typeof RealEstateListingsResponse>;

/** POST /api/v1/real-estate/plots/:id/purchase (requires Idempotency-Key). */
export const PlotPurchaseRequest = z
  .object({
    unit: z.literal("COIN"),
    /** The price the buyer saw; a different current price returns PRICE_CHANGED (409), never a silent charge. */
    expectedPriceCoins: z.number().int().min(0),
  })
  .strict();
export type PlotPurchaseRequest = z.infer<typeof PlotPurchaseRequest>;

export const PlotPurchaseResponse = z.object({
  orderId: Uuid,
  status: z.literal("fulfilled"),
  propertyId: PropertyId,
  ownershipIntervalId: Uuid,
  spentCoins: z.number().int().min(0),
  coinsAvailableAfter: z.number().int().min(0),
});
export type PlotPurchaseResponse = z.infer<typeof PlotPurchaseResponse>;

// ---------------- Store marketing board ----------------
/** PATCH /api/v1/properties/:id/interior-board (current commercial owner, or staff on Townsy-owned demo stores). */
export const InteriorBoardUpdateRequest = z
  .object({
    title: z.string().min(1).max(60),
    body: z.string().max(280),
    /** Version the editor started from (0 when no board exists yet); a mismatch returns PROPERTY_VERSION_CHANGED. */
    expectedVersion: z.number().int().min(0),
  })
  .strict();
export type InteriorBoardUpdateRequest = z.infer<typeof InteriorBoardUpdateRequest>;
export const InteriorBoardUpdateResponse = z.object({ propertyId: PropertyId, board: InteriorBoard });
export type InteriorBoardUpdateResponse = z.infer<typeof InteriorBoardUpdateResponse>;

/** PUT /api/v1/properties/:propertyId/interior-theme (current commercial owner; free; audited; idempotent by value). */
export const InteriorThemeUpdateRequest = z.object({ theme: StoreThemeId }).strict();
export type InteriorThemeUpdateRequest = z.infer<typeof InteriorThemeUpdateRequest>;
export const InteriorThemeUpdateResponse = z.object({ propertyId: PropertyId, interiorTheme: StoreThemeId, version: z.number().int().min(0) });
export type InteriorThemeUpdateResponse = z.infer<typeof InteriorThemeUpdateResponse>;

// ---------------- Avatar ----------------
/** PUT /api/v1/me/appearance (residents; guests keep their server-assigned random look). */
export const AppearanceUpdateRequest = AppearanceChoice;
export type AppearanceUpdateRequest = z.infer<typeof AppearanceUpdateRequest>;
